import { useMemo, type RefObject } from "react";
import type { BoardColumn, Task } from "../types";
import type { ListlessGroupPlacement } from "../lib/listlessGroupPlacement";
import { useFlipAnimation } from "./useFlipAnimation";

interface UseBoardColumnFlipArgs {
  containerRef: RefObject<HTMLElement | null>;
  columns: BoardColumn[];
  listlessTasks: Task[];
  matchingTaskIds: Set<string>;
  listlessPlacement: ListlessGroupPlacement;
  isCollapsed: (listId: string | undefined) => boolean;
  overTaskId: string | null;
  overEndZoneListId: string | null;
}

/**
 * Slide the board's lists to their new places only when what the board
 * shows changes: which lists and tasks, a list folded or opened, a drop spot
 * during a drag. Any other redraw — opening or closing a task — must not
 * slide them: a filter's hidden cards shrink after the redraw that hid them,
 * so the lists move with no redraw to save their new places, and the next
 * redraw would slide them in from where they used to be.
 */
export function useBoardColumnFlip({
  containerRef,
  columns,
  listlessTasks,
  matchingTaskIds,
  listlessPlacement,
  isCollapsed,
  overTaskId,
  overEndZoneListId,
}: UseBoardColumnFlipArgs): void {
  // The drop spot changes on every drag move, so it stays out of this memo:
  // a move does not walk every task again.
  const tasksKey = useMemo(() => {
    const visibleIds = (columnTasks: Task[]) =>
      columnTasks
        .filter((t) => matchingTaskIds.has(t.id))
        .map((t) => t.id)
        .join(",");
    return [
      listlessPlacement,
      ...columns.map(
        (column) =>
          `${column.name}${isCollapsed(column.listId) ? " (collapsed)" : ""}:${visibleIds(column.tasks)}`,
      ),
      `listless:${visibleIds(listlessTasks)}`,
    ].join("|");
  }, [columns, listlessTasks, matchingTaskIds, listlessPlacement, isCollapsed]);
  const orderKey = `${tasksKey}|drop:${overTaskId ?? ""}:${overEndZoneListId ?? ""}`;
  useFlipAnimation(containerRef, "[data-flip-column-id]", orderKey);
}
